import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components/macro'
import { useTranslation } from 'react-i18next'
import Link from 'components/atoms/link'
import EnhancedTable, {
  TableDataText,
} from 'components/organisms/enhanced-table'
import Label from 'components/atoms/label'
import Typography from 'components/molecules/typography'
import Button from 'components/atoms/button'
import { thuistaxatieStatuses } from 'config/data'
import { formatDate, formatLicenseplate } from 'utilities/format'
import { triggerRemindMessageRequest } from 'utilities/data'
import toast from 'utilities/toast'
import { API_ERROR_MESSAGES } from 'utilities/api'

const StyledTableDataText = styled(TableDataText)`
  margin-top: 0;
`

const StyledTableWrapper = styled.div`
  background-color: ${({ theme }) => theme.colors.defaultBackground};
  padding: ${({ theme }) => theme.sizings.lvl3};
  border-radius: ${({ theme }) =>
    theme.cardBorderRadius ? `${theme.cardBorderRadius}px` : theme.sizings.lvl0};
  margin-top: ${({ theme }) => theme.sizings.lvl2};

  td {
    vertical-align: middle;
  }
`

const ResultsCount = styled.div`
  margin-bottom: ${({ theme }) => theme.sizings.lvl2};
  color: ${({ theme }) => theme.colors.chartsTick};
  font-size: ${({ theme }) => theme.baseFontSize};
`

const StyledLabelWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
`

const RemindButton = styled(Button)`
  padding: ${({ theme }) => theme.sizings.lvl0}
    ${({ theme }) => theme.sizings.lvl1};
  font-size: ${({ theme }) => theme.smallFontSize};
`

const ThuistaxatieTable = ({ items, error }) => {
  const { t } = useTranslation()

  const handleRemindClick = async (requestId) => {
    try {
      const success = await triggerRemindMessageRequest(null, requestId)
      if (success) {
        toast.success(t('thuistaxatie.remindSuccess'))
      }
    } catch (error) {
      if (error === API_ERROR_MESSAGES.UNKNOWN_ERROR) {
        toast.success(t('thuistaxatie.remindSuccess'))
      } else {
        toast.error(t('thuistaxatie.remindError'))
      }
    }
  }

  if (error) {
    return (
      <Typography type="BodyText" color="error">
        {t('common.errors.loading')}
      </Typography>
    )
  }

  if (!items || items.length === 0) {
    return <Typography type="BodyText">{t('thuistaxatie.noData')}</Typography>
  }
  const sortedItems = items.sort(
    (a, b) => new Date(b.created_at) - new Date(a.created_at),
  )
  const formattedRows = sortedItems.map((item) => ({
    licenseplate: {
      component: (
        <StyledTableDataText>
          {item.car_id > 0 ? (
            <Link to={`/car/${item.car_id}`}>
              {formatLicenseplate(item.lead_info.licenseplate)}
            </Link>
          ) : (
            formatLicenseplate(item.lead_info.licenseplate)
          )}
        </StyledTableDataText>
      ),
      data: item.lead_info.licenseplate,
    },
    firstname: {
      component: (
        <StyledTableDataText>{item.lead_info.firstname}</StyledTableDataText>
      ),
      data: item.lead_info.firstname,
    },
    lastname: {
      component: (
        <StyledTableDataText>{item.lead_info.lastname}</StyledTableDataText>
      ),
      data: item.lead_info.lastname,
    },
    phone: {
      component: (
        <StyledTableDataText>{item.lead_info.phone}</StyledTableDataText>
      ),
      data: item.lead_info.phone,
    },
    mileage: {
      component: (
        <StyledTableDataText>
          {item.lead_info.mileage || '-'}
        </StyledTableDataText>
      ),
      data: item.lead_info.mileage,
    },
    created_at: {
      component: (
        <StyledTableDataText>{formatDate(item.created_at)}</StyledTableDataText>
      ),
      data: item.created_at,
    },
    status: {
      component: (
        <StyledLabelWrapper>
          <Label
            text={
              item.car_id !== 0
                ? t('receivedCarsTab.title')
                : t('sentCarsTab.title')
            }
            backgroundColor={
              item.car_id !== 0
                ? thuistaxatieStatuses.RECEIVED
                : thuistaxatieStatuses.SENT
            }
          />
        </StyledLabelWrapper>
      ),
      data: item.status,
    },
    remind: {
      component: (
        <RemindButton
          level="option"
          size="small"
          disabled={item.car_id !== 0}
          onClick={() => handleRemindClick(item.id)}
        >
          {t('sendReminder')}
        </RemindButton>
      ),
      data: 'remind',
    },
  }))

  return (
    <StyledTableWrapper>
      <ResultsCount>
        {items.length} {t('results').toLowerCase()}
      </ResultsCount>
      <EnhancedTable
        selectable={false}
        columns={[
          {
            id: 'licenseplate',
            label: t('licensePlate'),
            width: '12%',
          },
          {
            id: 'firstname',
            label: t('firstName'),
            width: '12%',
          },
          {
            id: 'lastname',
            label: t('lastName'),
            width: '12%',
          },
          {
            id: 'phone',
            label: t('phoneNumber'),
            width: '12%',
          },
          {
            id: 'mileage',
            label: t('mileage'),
            width: '12%',
          },
          {
            id: 'created_at',
            label: t('thuistaxatie.tableColumns.createdAt'),
            width: '15%',
          },
          {
            id: 'status',
            label: t('status'),
            width: '10%',
          },
          {
            id: 'remind',
            label: t('thuistaxatie.tableColumns.sendReminder'),
            width: '15%',
          },
        ]}
        rows={formattedRows}
      />
    </StyledTableWrapper>
  )
}

ThuistaxatieTable.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      lead_info: PropTypes.shape({
        licenseplate: PropTypes.string.isRequired,
        firstname: PropTypes.string.isRequired,
        lastname: PropTypes.string.isRequired,
        phone: PropTypes.string.isRequired,
      }).isRequired,
      created_at: PropTypes.string.isRequired,
    }),
  ),
  error: PropTypes.any,
}

ThuistaxatieTable.defaultProps = {
  items: [],
  error: null,
}

export default ThuistaxatieTable
